import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  IconButton,
  InputAdornment,
  Grid,
  Alert,
  Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../context/AuthContext';

function CallRates() {
  const { currentUser } = useAuth();
  const [callRates, setCallRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check if current user is admin
  const isAdmin = currentUser?.role === 'admin';
  const isMerchant = currentUser?.role === 'merchant';
  
  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  // Form states
  const [currentRate, setCurrentRate] = useState(null);
  const [formData, setFormData] = useState({
    country_name: '',
    country_code: '',
    country_short_name: '',
    inbound_rate: 0,
    outbound_rate: 0,
    ooak_inbound_rate: 0,
    ooak_outbound_rate: 0,
    currency: 'JPY' // Changed default currency from USD to JPY
  });
  
  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  useEffect(() => {
    fetchCallRates();
  }, []);
  
  const fetchCallRates = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/call-rates');
      setCallRates(response.data.callRates);
      setLoading(false);
    } catch (err) {
      setError('Failed to load call rates');
      setLoading(false);
      console.error('Call rates fetch error:', err);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleAddDialogOpen = () => {
    setFormData({
      country_name: '',
      country_code: '',
      country_short_name: '',
      inbound_rate: 0,
      outbound_rate: 0,
      ooak_inbound_rate: 0,
      ooak_outbound_rate: 0,
      currency: 'JPY' // Changed default currency from USD to JPY
    });
    setOpenAddDialog(true);
  };
  
  const handleEditDialogOpen = (rate) => {
    if (!rate || !rate.id) {
      console.error('Invalid rate object passed to handleEditDialogOpen:', rate);
      return;
    }
    setCurrentRate(rate);
    setFormData({
      country_name: rate.country_name,
      country_code: rate.country_code,
      country_short_name: rate.country_short_name,
      inbound_rate: rate.inbound_rate,
      outbound_rate: rate.outbound_rate,
      ooak_inbound_rate: rate.ooak_inbound_rate,
      ooak_outbound_rate: rate.ooak_outbound_rate,
      currency: rate.currency
    });
    setOpenEditDialog(true);
  };
  
  const handleDeleteDialogOpen = (rate) => {
    setCurrentRate(rate);
    setOpenDeleteDialog(true);
  };
  
  const handleDialogClose = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setCurrentRate(null);
  };
  
  const handleAddRate = async () => {
    try {
      const response = await axios.post('/call-rates', formData);
      
      if (response.data.success) {
        setCallRates([...callRates, response.data.callRate]);
        handleDialogClose();
        showNotification('Call rate added successfully', 'success');
      }
    } catch (err) {
      console.error('Error adding call rate:', err);
      showNotification(err.response?.data?.message || 'Failed to add call rate', 'error');
    }
  };
  
  const handleEditRate = async () => {
    try {
      const response = await axios.put(`/call-rates/${currentRate.id}`, formData);
      
      if (response.data.success) {
        setCallRates(callRates.map(rate => 
          rate.id === currentRate.id ? response.data.callRate : rate
        ));
        handleDialogClose();
        showNotification('Call rate updated successfully', 'success');
      }
    } catch (err) {
      console.error('Error updating call rate:', err);
      showNotification(err.response?.data?.message || 'Failed to update call rate', 'error');
    }
  };
  
  const handleDeleteRate = async () => {
    try {
      const response = await axios.delete(`/call-rates/${currentRate.id}`);
      
      if (response.data.success) {
        setCallRates(callRates.filter(rate => rate.id !== currentRate.id));
        handleDialogClose();
        showNotification('Call rate deleted successfully', 'success');
      }
    } catch (err) {
      console.error('Error deleting call rate:', err);
      showNotification(err.response?.data?.message || 'Failed to delete call rate', 'error');
    }
  };
  
  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };
  
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Call Rates by Country {isMerchant && '(Read Only)'}
        </Typography>
        {/* Only show Add button for admin users */}
        {isAdmin && (
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />} 
            onClick={handleAddDialogOpen}
          >
            Add New Rate
          </Button>
        )}
      </Box>
      
      {/* Show info message for merchant users */}
      {isMerchant && (
        <Box mb={3} p={2} sx={{ backgroundColor: '#fff3cd', borderRadius: 2, border: '1px solid #ffeaa7' }}>
          <Typography variant="body2" color="textSecondary">
            You have read-only access to call rates. Contact your administrator to request changes.
          </Typography>
        </Box>
      )}
      
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="call rates table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Country Code</TableCell>
              <TableCell>Country Short Name</TableCell>
              <TableCell>Inbound Rate (¥)</TableCell> {/* Changed USD to ¥ */}
              <TableCell>Outbound Rate (¥)</TableCell> {/* Changed USD to ¥ */}
              <TableCell>OOAK Inbound (¥)</TableCell> {/* Changed USD to ¥ */}
              <TableCell>OOAK Outbound (¥)</TableCell> {/* Changed USD to ¥ */}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {callRates.map((rate) => (
              <TableRow key={rate.id || Math.random()}>
                <TableCell>{rate.id}</TableCell>
                <TableCell>{rate.country_name}</TableCell>
                <TableCell>{rate.country_code}</TableCell>
                <TableCell>{rate.country_short_name}</TableCell>
                <TableCell>¥{parseFloat(rate.inbound_rate).toFixed(2)}</TableCell> {/* Changed $ to ¥ and decimal places from 5 to 2 */}
                <TableCell>¥{parseFloat(rate.outbound_rate).toFixed(2)}</TableCell> {/* Changed $ to ¥ and decimal places from 5 to 2 */}
                <TableCell>¥{parseFloat(rate.ooak_inbound_rate).toFixed(2)}</TableCell> {/* Changed $ to ¥ and decimal places from 5 to 2 */}
                <TableCell>¥{parseFloat(rate.ooak_outbound_rate).toFixed(2)}</TableCell> {/* Changed $ to ¥ and decimal places from 5 to 2 */}
                <TableCell>
                  {/* Only show action buttons for admin users */}
                  {isAdmin ? (
                    <>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEditDialogOpen(rate)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteDialogOpen(rate)} 
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      View Only
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {callRates.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">No call rates found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Add Call Rate Dialog */}
      <Dialog open={openAddDialog} onClose={handleDialogClose}>
        <DialogTitle>Add New Call Rate</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                name="country_name"
                label="Country Name"
                fullWidth
                value={formData.country_name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="country_code"
                label="Country Code"
                fullWidth
                value={formData.country_code}
                onChange={handleInputChange}
                required
                placeholder="+xx"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="country_short_name"
                label="Country Short Name"
                fullWidth
                value={formData.country_short_name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="inbound_rate"
                label="Inbound Rate"
                fullWidth
                value={formData.inbound_rate}
                onChange={handleInputChange}
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="outbound_rate"
                label="Outbound Rate"
                fullWidth
                value={formData.outbound_rate}
                onChange={handleInputChange}
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="ooak_inbound_rate"
                label="OOAK Inbound Rate"
                fullWidth
                value={formData.ooak_inbound_rate}
                onChange={handleInputChange}
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="ooak_outbound_rate"
                label="OOAK Outbound Rate"
                fullWidth
                value={formData.ooak_outbound_rate}
                onChange={handleInputChange}
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="currency"
                label="Currency"
                fullWidth
                value={formData.currency}
                onChange={handleInputChange}
                placeholder="JPY"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleAddRate} color="primary" variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Call Rate Dialog */}
      <Dialog open={openEditDialog} onClose={handleDialogClose}>
        <DialogTitle>Edit Call Rate</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                name="country_name"
                label="Country Name"
                fullWidth
                value={formData.country_name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="country_code"
                label="Country Code"
                fullWidth
                value={formData.country_code}
                onChange={handleInputChange}
                required
                placeholder="+xx"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="country_short_name"
                label="Country Short Name"
                fullWidth
                value={formData.country_short_name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="inbound_rate"
                label="Inbound Rate"
                fullWidth
                value={formData.inbound_rate}
                onChange={handleInputChange}
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="outbound_rate"
                label="Outbound Rate"
                fullWidth
                value={formData.outbound_rate}
                onChange={handleInputChange}
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="ooak_inbound_rate"
                label="OOAK Inbound Rate"
                fullWidth
                value={formData.ooak_inbound_rate}
                onChange={handleInputChange}
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="ooak_outbound_rate"
                label="OOAK Outbound Rate"
                fullWidth
                value={formData.ooak_outbound_rate}
                onChange={handleInputChange}
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="currency"
                label="Currency"
                fullWidth
                value={formData.currency}
                onChange={handleInputChange}
                placeholder="JPY"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleEditRate} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleDialogClose}>
        <DialogTitle>Delete Call Rate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the call rate for {currentRate?.country_name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteRate} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Notification Snackbar */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CallRates;